import {computed, inject, Injectable, signal, Signal} from '@angular/core';
import {Retailer} from '@features/leads/retailers/retailers.model';
import {RetailersService} from '@features/leads/retailers/retailers.service';
import {tap} from "rxjs";
import {finalize} from "rxjs/operators";

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

interface OfferingCache {
  retailers: Retailer[];
  state: LoadingState;
}

@Injectable({providedIn: 'root'})
export class RetailerCacheService {
  private readonly retailersService = inject(RetailersService);
  private readonly cache = signal(new Map<string, OfferingCache>(new Map()));

  getLoadingState(offeringUuid: string): Signal<LoadingState> {
    return computed(() => this.cache().get(offeringUuid)?.state ?? 'idle');
  }

  getRetailersSignal(offeringUuid: string): Signal<Retailer[]> {
    return computed(() => this.cache().get(offeringUuid)?.retailers ?? []);
  }

  initialize(offeringUuid: string): void {
    this.lazyLoad(offeringUuid);
  }

  refresh(offeringUuid: string): void {
    this.fetchAndStore(offeringUuid);
  }

  addRetailer(offeringUuid: string, retailer: Retailer): void {
    this.updateEntry(offeringUuid, entry => ({...entry, retailers: [retailer, ...entry.retailers]}));
  }

  // 'loading' | 'loaded' | 'error' → do nothing, fetch already in flight or done
  private lazyLoad(offeringUuid: string): void {
    const state = this.cache().get(offeringUuid)?.state;
    if (!state || state === 'idle') {
      this.fetchAndStore(offeringUuid);
    }
  }

  private fetchAndStore(offeringUuid: string): void {
    this.updateEntry(offeringUuid, entry => ({...entry, state: 'loading'}));

    this.retailersService.getAll().pipe(
      tap(retailers => {
        this.updateEntry(offeringUuid, entry => ({
          ...entry,
          retailers,
          state: 'loaded',
        }));
      }),
      finalize(() => {
        // If state is still 'loading' after finalize, the tap never ran → error path
        const state = this.cache().get(offeringUuid)?.state;
        if (state === 'loading') {
          this.updateEntry(offeringUuid, entry => ({...entry, state: 'error'}));
        }
      })
    ).subscribe({
      error: err => console.error(`[RetailerCache] Failed to load for ${offeringUuid}`, err)
    })
  }

  private updateEntry(
    offeringUuid: string,
    updater: (entry: OfferingCache) => OfferingCache
  ): void {
    const current = this.cache();
    const existing = current.get(offeringUuid) ?? {retailers: [], state: 'idle' as LoadingState};
    const next = new Map(current);
    next.set(offeringUuid, updater(existing));
    this.cache.set(next);
  }
}
